import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCustomerProfile, getCustomerAddresses, getExpressSettingsApi } from "../../../../api";
import { useBooking } from "../../../../context/BookingContext";
import AddressForm from "../../../Booking/AddressForm";
import useAuthGuard from "../../../../hooks/useAuthGuard";
import styles from "./GripBookingDetails.module.css";
import { resolveGripFromBC } from "../../../../utils/gripResolver";

function joinAddress(...parts) {
  return parts
    .map((p) => (p ?? "").toString().trim())
    .filter((p) => p && p.toLowerCase() !== "null")
    .join(", ");
}

function ServiceSelect({ value, onChange, mode, disabled = false }) {
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef(null);
  const listRef = React.useRef(null);

  const commonInStore = React.useMemo(
    () => [
      {
        value: "in-store",
        label: <>In-Store (No Charge)</>,
        plainTextLabel: "In-Store (No Charge)",
      },
    ],
    []
  );

  const regularOptions = React.useMemo(
    () => [
      ...commonInStore,
      {
        value: "pickup",
        label: (
          <>
            Pickup Only (+ <span className="dirham-symbol">&#xea;</span> 30)
          </>
        ),
        plainTextLabel: "Pickup Only (+ 30)",
      },
      {
        value: "delivery",
        label: (
          <>
            Delivery Only (+ <span className="dirham-symbol">&#xea;</span> 30)
          </>
        ),
        plainTextLabel: "Delivery Only (+ 30)",
      },
      {
        value: "pickup/delivery",
        label: (
          <>
            Both Pickup &amp; Delivery (+{" "}
            <span className="dirham-symbol">&#xea;</span> 60)
          </>
        ),
        plainTextLabel: "Both Pickup & Delivery (+ 60)",
      },
    ],
    [commonInStore]
  );

  const expressOptions = React.useMemo(
    () => [
      ...commonInStore,
      {
        value: "express-pickup",
        label: (
          <>
            Express Pickup (+ <span className="dirham-symbol">&#xea;</span> 50)
          </>
        ),
        plainTextLabel: "Express Pickup (+ 50)",
      },
      {
        value: "express-delivery",
        label: (
          <>
            Express Delivery (+ <span className="dirham-symbol">&#xea;</span>{" "}
            50)
          </>
        ),
        plainTextLabel: "Express Delivery (+ 50)",
      },
      {
        value: "express-pickup/delivery",
        label: (
          <>
            Express Pickup &amp; Delivery (+{" "}
            <span className="dirham-symbol">&#xea;</span> 100)
          </>
        ),
        plainTextLabel: "Express Pickup & Delivery (+ 100)",
      },
    ],
    [commonInStore]
  );

  const options = React.useMemo(
    () => (mode === "Express" ? expressOptions : regularOptions),
    [mode, expressOptions, regularOptions]
  );

  const current = options.find((o) => o.value === value) || options[0];

  React.useEffect(() => {
    if (!options.find((o) => o.value === value)) {
      onChange("in-store");
    }
  }, [options, value, onChange]);

  const [activeIndex, setActiveIndex] = React.useState(
    Math.max(
      0,
      options.findIndex((o) => o.value === value)
    )
  );

  React.useEffect(() => {
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((o) => o.value === value)
      )
    );
  }, [options, value]);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!buttonRef.current || !listRef.current) return;
      if (
        !buttonRef.current.contains(e.target) &&
        !listRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const openList = () => {
    setOpen(true);
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((o) => o.value === value)
      )
    );
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = options[activeIndex];
      if (opt) {
        onChange(opt.value);
        setOpen(false);
      }
    }
  };

  return (
    <div className={styles.customSelect}>
      <button
        type="button"
        ref={buttonRef}
        className={styles.selectButton}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (disabled ? null : (open ? setOpen(false) : openList()))}
        onKeyDown={disabled ? undefined : onKeyDown}
        disabled={disabled}
      >
        <span className={styles.selectButtonLabel}>{current.label}</span>
        <span className={styles.selectCaret}>▾</span>
      </button>

      {open && (
        <ul
          className={styles.options}
          role="listbox"
          aria-activedescendant={`opt-${activeIndex}`}
          tabIndex={-1}
          ref={listRef}
        >
          {options.map((opt, idx) => (
            <li
              key={opt.value}
              id={`opt-${idx}`}
              role="option"
              aria-selected={opt.value === value}
              className={`${styles.option} ${
                opt.value === value ? styles.optionSelected : ""
              } ${idx === activeIndex ? styles.optionActive : ""}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const GripBookingDetails = () => {
  useAuthGuard();
  const { setPickupService, address, setAddress } = useBooking();
  const [validationError, setValidationError] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const {
    serviceKind = "grip",
    bookingDate = "",
    gripType = "Regular",
    gripId = null,
    gripPrice = null,
    rp_id = null,
    lockServiceSelection = false,
    pickupService: carriedPickupService = null,
    instoreLocation: carriedInstoreLocation = "",
    address: carriedAddress = null,
    racketName = "",
    bc_product_id = null,
    bc_variant_id = null,
  } = location.state || {};

  const [pickupOption, setPickupOption] = useState(
    carriedPickupService || localStorage.getItem("pickupService") || "in-store"
  );
  const [instoreLocation, setInstoreLocation] = useState(
    carriedInstoreLocation || ""
  );
  const [racketNameInput, setRacketNameInput] = useState(racketName);
  const [dubaiHillsExpressEnabled, setDubaiHillsExpressEnabled] = useState(false);

  useEffect(() => {
    getExpressSettingsApi()
      .then((res) => {
        if (res?.data) setDubaiHillsExpressEnabled(Boolean(res.data.dubaihills_express_enabled));
      })
      .catch((err) => {
        console.error("Could not fetch express settings:", err);
      });
  }, []);
  const isDubaiHillsExpressBlocked = gripType === "Express" && !dubaiHillsExpressEnabled;
  const [loadingAddresses, setLoadingAddresses] = useState(true);

  // Load profile
  useEffect(() => {
    (async () => {
      try {
        const response = await getCustomerProfile();
        setCustomerName(
          `${response.data.first_name} ${response.data.last_name}`
        );
        setCustomerId(response.data.customer_id);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    })();
  }, []);

  // Load default address
  useEffect(() => {
    if (customerId) {
      // If address was carried from previous page, use it and skip fetching
      if (carriedAddress) {
        setAddress(carriedAddress);
        setLoadingAddresses(false);
        return;
      }

      setLoadingAddresses(true);
      getCustomerAddresses(customerId)
        .then((response) => {
          const customerAddresses = response.data;
          if (!customerAddresses || customerAddresses.length === 0) {
            setAddress("");
            return;
          }
          const def = customerAddresses.find((a) => a.is_default === 1);
          if (def) {
            setAddress(
              `${def.address_line}, ${def.area}, ${def.pincode}, ${def.state}, ${def.country}`
            );
          } else {
            setAddress("");
          }
        })
        .catch((err) => console.error("Error fetching addresses:", err))
        .finally(() => setLoadingAddresses(false));
    }
  }, [customerId, setAddress, carriedAddress]);

  const handleNextClick = async () => {
    if (!racketNameInput.trim()) {
      setValidationError("Please enter a racket name.");
      return;
    }

    if (!lockServiceSelection) {
      if (pickupOption === "in-store" && !instoreLocation) {
        setValidationError(
          "Please select a store location for in-store booking."
        );
        return;
      }
      if (pickupOption === "in-store" && instoreLocation?.toLowerCase().includes("marina") && gripType === "Express") {
        setValidationError(
          "Express service is not available at the Marina Store. Please choose Jumeirah Store or regular service."
        );
        return;
      }
      if (pickupOption === "in-store" && instoreLocation?.toLowerCase().includes("dubai") && isDubaiHillsExpressBlocked) {
        setValidationError(
          "Express service is not available at the Dubai Hills Store. Please choose Jumeirah Store or regular service."
        );
        return;
      }
      if (pickupOption !== "in-store" && !address.trim()) {
        setValidationError("Please enter an address for the selected service.");
        return;
      }
    }

    setPickupService(pickupOption);
    setValidationError("");
    let finalGripId = gripId;
    if (!finalGripId && bc_product_id) {
      const resolved = await resolveGripFromBC({
        productId: bc_product_id,
        variantId: bc_variant_id,
        serviceType: gripType,
      });
      if (!resolved) {
        setValidationError(
          "Could not map product to a grip. Please choose again."
        );
        console.error("[GripBookingDetails] mapping failed", {
          bc_product_id,
          bc_variant_id,
          gripType,
        });
        return;
      }
      finalGripId = resolved.gripId;
      console.log("[GripBookingDetails] mapped:", resolved);
    }
    navigate("/customer/dashboard/grip-booking-final", {
      state: {
        serviceKind,
        customerName,
        customerId,
        bookingDate,
        pickupService: pickupOption,
        instoreLocation,
        address,
        racketName: racketNameInput,

        gripId: finalGripId,
        gripType,
        bc_product_id,
        bc_variant_id,
        gripPrice,
        rp_id,
        previousBookings: JSON.parse(localStorage.getItem("bookings") || "[]"),
      },
    });
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Create Booking</h2>

      <div className={styles.infoContainer}>
        <div className={styles.infoBlock}>
          <label className={styles.label}>Customer Name:</label>
          <span className={styles.disabledText}>
            {customerName || "Loading..."}
          </span>
        </div>
        <div className={styles.infoBlock}>
          <label className={styles.label}>Service:</label>
          <span className={styles.disabledText}>Grip Service</span>
        </div>
        <div className={styles.infoBlock}>
          <label className={styles.label}>Booking Date:</label>
          <input
            type="date"
            className={styles.dateInput}
            value={bookingDate}
            disabled
          />
        </div>
      </div>

      <div className={styles.infoContainer}>
        <div className={styles.infoBlock}>
          <label className={styles.label}>Choose Service:</label>
          <ServiceSelect
            value={pickupOption}
            onChange={setPickupOption}
            mode={gripType}
            disabled={lockServiceSelection}
          />

          {pickupOption === "in-store" && (
            <div className={styles.infoBlock}>
              <label className={styles.label}>Choose Store:</label>
              <select
                value={instoreLocation}
                onChange={(e) => setInstoreLocation(e.target.value)}
                className={styles.select}
                disabled={lockServiceSelection}
              >
                <option value="">-- Select a Store --</option>
                <option value="Jumeirah Store">Jumeirah Store</option>
                <option value="Marina Store" disabled={gripType === "Express"}>
                  Marina Store {gripType === "Express" ? "(Express Not Available)" : ""}
                </option>
                <option value="Dubai Hills Store" disabled={isDubaiHillsExpressBlocked}>
                  Dubai Hills Store {isDubaiHillsExpressBlocked ? "(Express Not Available)" : ""}
                </option>
              </select>
              {instoreLocation === "Marina Store" && gripType === "Express" && (
                <span style={{ fontSize: "0.825rem", color: "#c2410c", marginTop: "6px", display: "block" }}>
                  ⚠️ Express service is not available at Marina Store. Please select Jumeirah Store.
                </span>
              )}
            </div>
          )}

          {pickupOption !== "in-store" && !lockServiceSelection && (
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className={styles.addAddressButton}
            >
              {address ? "Change Address" : "Add Address"}
            </button>
          )}
        </div>
      </div>

      {loadingAddresses &&
        pickupOption !== "in-store" &&
        !lockServiceSelection && (
          <p className={styles.loadingText}>Loading addresses...</p>
        )}

      {address && pickupOption !== "in-store" && !loadingAddresses && (
        <div className={styles.infoBlock}>
          <label className={styles.label}>Selected Address:</label>
          <span className={styles.disabledText}>{address}</span>
        </div>
      )}

      <div className={styles.formGroup}>
        <label className={styles.label}>Racket Name:</label>
        <input
          className={styles.inputField}
          value={racketNameInput}
          onChange={() => {}}
          placeholder="Enter racket name"
          disabled
          readOnly
          aria-readonly="true"
          title="Racket name is set in the previous step"
        />
      </div>

      {validationError && (
        <p className={styles.validationError}>{validationError}</p>
      )}

      <div className={styles.buttonGroup}>
        <button className={styles.cancelButton} onClick={() => navigate(-1)}>
          Cancel
        </button>
        <button className={styles.nextButton} onClick={handleNextClick}>
          Next
        </button>
      </div>

      {isModalOpen && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Select or Add Address</h3>
            <AddressForm
              onSubmit={(addr) => {
                const formattedAddress = joinAddress(
                  addr.address_line,
                  addr.area,
                  addr.pincode,
                  addr.state,
                  addr.country
                );
                setAddress(formattedAddress);
                setIsModalOpen(false);
              }}
              customerId={customerId}
            />
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className={styles.closeModalButton}
            >
              ✖
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GripBookingDetails;
